// client/legend-cms/src/worldCupMatches/WorldCupMatchCreate.js
import React, { useEffect, useState } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  DateTimeInput,
  NumberInput,
  SelectInput,
  ReferenceInput,
  required,
  minValue,
} from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

const useStyles = makeStyles({
  inline: {
    display: 'inline-block',
    marginLeft: '1em',
    '&.first-child': {
      marginLeft: 0,
    },
  },
});

const statusChoices = [
  { id: 'SCHEDULED', name: 'Agendado' },
  { id: 'OPEN', name: 'Aberto para palpites' },
  { id: 'CLOSED', name: 'Fechado para palpites' },
];

const getAuthHeaders = () => {
  const { access_token } = JSON.parse(
    localStorage.getItem('auth') || '{"access_token": ""}',
  );

  return { Authorization: `Bearer ${access_token}` };
};

// Pré-seleciona a campanha ativa no formulário de criação, para o caso
// comum de só existir uma campanha em vigor. Se a busca falhar ou não
// houver campanha ativa, o campo simplesmente fica vazio e o backend usa
// a campanha ativa como fallback ao salvar (ver PredictionCampaignCmsController).
const useActiveCampaignId = () => {
  const [activeCampaignId, setActiveCampaignId] = useState(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchActiveCampaign = async () => {
      try {
        const filter = encodeURIComponent(JSON.stringify({ active: true }));
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/cms/prediction-campaigns?filter=${filter}&range=[0,0]&sort=["id","DESC"]`,
          { headers: getAuthHeaders() },
        );
        const json = await response.json();

        if (isMounted && Array.isArray(json) && json[0]) {
          setActiveCampaignId(json[0].id);
        }
      } catch (error) {
        // silencioso: o campo fica vazio e o backend aplica o fallback
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchActiveCampaign();

    return () => {
      isMounted = false;
    };
  }, []);

  return { activeCampaignId, loading };
};

const WorldCupMatchCreate = (props) => {
  const classes = useStyles();
  const { activeCampaignId, loading } = useActiveCampaignId();

  if (loading) {
    return null;
  }

  return (
    <Create title="Cadastrar jogo da Copa" {...props}>
      <SimpleForm initialValues={{ campaign_id: activeCampaignId }}>
        <ReferenceInput
          source="campaign_id"
          reference="prediction-campaigns"
          label="Campanha"
          sort={{ field: 'id', order: 'DESC' }}
          helperText="Se vazio, o backend usa a campanha ativa no momento do envio."
          fullWidth
        >
          <SelectInput optionText="title" />
        </ReferenceInput>

        <div />

        <TextInput
          source="title"
          label="Título"
          helperText="Se vazio, será gerado automaticamente. Ex: Brasil x Argentina"
          fullWidth
        />

        <ReferenceInput
          source="home_team_id"
          reference="prediction-teams"
          label="Time mandante"
          filter={{ active: true }}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={500}
          validate={required()}
          formClassName={clsx(classes.inline, 'first-child')}
        >
          <SelectInput optionText="name" />
        </ReferenceInput>

        <ReferenceInput
          source="away_team_id"
          reference="prediction-teams"
          label="Time visitante"
          filter={{ active: true }}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={500}
          validate={required()}
          formClassName={classes.inline}
        >
          <SelectInput optionText="name" />
        </ReferenceInput>

        <div />

        <DateTimeInput
          source="match_date"
          label="Data e hora do jogo"
          validate={required()}
          formClassName={clsx(classes.inline, 'first-child')}
        />

        <DateTimeInput
          source="prediction_deadline"
          label="Limite para palpites"
          formClassName={classes.inline}
        />

        <div />

        <SelectInput
          source="status"
          label="Status"
          choices={statusChoices}
          defaultValue="SCHEDULED"
          validate={required()}
        />

        <div />

        <NumberInput
          source="exact_score_xp"
          label="XP por placar exato"
          defaultValue={100}
          validate={[required(), minValue(0)]}
          formClassName={clsx(classes.inline, 'first-child')}
        />

        <NumberInput
          source="correct_winner_xp"
          label="XP por vencedor/empate correto"
          defaultValue={50}
          validate={[required(), minValue(0)]}
          formClassName={classes.inline}
        />

        <NumberInput
          source="participation_xp"
          label="XP por participação"
          defaultValue={10}
          validate={[required(), minValue(0)]}
          formClassName={classes.inline}
        />
      </SimpleForm>
    </Create>
  );
};

export default WorldCupMatchCreate;
